import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Shield, Database, Workflow, Lock, CheckCircle2, XCircle, Link2, Layers, Cable, Cloud, FileCheck2, Calendar, Zap, Users, KeyRound } from 'lucide-react';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: (i: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.05,
      duration: 0.5,
      ease: 'easeOut'
    }
  })
};
const SectionHeader = ({
  num,
  eyebrow,
  title,
  subtitle
}: {
  num: string;
  eyebrow?: string;
  title: string;
  subtitle?: string;
}) => <div className="mb-10" data-id="element-3259">
    <div className="flex items-baseline gap-4 mb-3" data-id="element-3260">
      <span className="font-mono text-sm text-red-600" data-id="element-3261">{num}</span>
      {eyebrow && <span className="font-mono text-xs uppercase tracking-widest text-stone-500" data-id="element-3262">
          {eyebrow}
        </span>}
    </div>
    <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-stone-50 leading-tight" data-id="element-3263">
      {title}
    </h2>
    {subtitle && <p className="text-stone-400 text-lg mt-4 max-w-3xl leading-relaxed" data-id="element-3264">
        {subtitle}
      </p>}
  </div>;
export function SalesforceIntegration() {
  return <div className="min-h-screen bg-stone-950 text-stone-300 font-sans selection:bg-red-600/30" data-id="element-3265">
      {/* Top Bar */}
      <div className="fixed top-0 left-0 right-0 h-16 bg-stone-950/85 backdrop-blur-md border-b border-stone-900 z-50 flex items-center justify-between px-6" data-id="element-3266">
        <Link to="/" className="flex items-center text-stone-400 hover:text-stone-50 transition-colors font-mono text-xs uppercase tracking-widest" data-id="element-3267">
          <ArrowLeft className="w-4 h-4 mr-2" data-id="element-3268" />
          Back to site
        </Link>
        <div className="flex items-center gap-3" data-id="element-3269">
          <div className="w-5 h-5 bg-red-600 flex items-center justify-center" data-id="element-3270">
            <span className="font-serif text-xs font-bold text-white" data-id="element-3271">B</span>
          </div>
          <span className="font-mono text-xs uppercase tracking-widest text-stone-500" data-id="element-3272">
            Salesforce Integration
          </span>
        </div>
      </div>

      {/* HERO */}
      <section className="pt-32 pb-20 border-b border-stone-900" data-id="element-3273">
        <div className="max-w-6xl mx-auto px-6" data-id="element-3274">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-3275">
            <div className="inline-flex items-center gap-2 px-3 py-1 border border-stone-800 bg-stone-900/50 font-mono text-xs uppercase tracking-widest text-stone-400 mb-8" data-id="element-3276">
              <Shield className="w-3.5 h-3.5 text-emerald-500" data-id="element-3277" />
              Salesforce-safe by design
            </div>

            <h1 className="font-serif text-5xl md:text-6xl tracking-tight text-stone-50 leading-[1.05] mb-6 max-w-4xl" data-id="element-3278">
              BlockQuote works <span className="text-red-600" data-id="element-3279">alongside</span>{' '}
              Salesforce — it doesn't replace it.
            </h1>

            <p className="text-xl text-stone-400 leading-relaxed max-w-3xl mb-10" data-id="element-3280">
              Salesforce remains your system of record. BlockQuote is the
              speed-to-quote workflow layer that syncs Deal ID, status, and
              timestamps back to Salesforce — minimal, reversible, and
              non-disruptive.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 py-24 space-y-32" data-id="element-3281">
        {/* SECTION 1 — Principle */}
        <section data-id="element-3282">
          <SectionHeader num="01" eyebrow="The Principle" title="Salesforce = System of Record. BlockQuote = Quoting Rail." subtitle="Two systems, two jobs. One clean handoff. Your team keeps Salesforce as the source of truth while BlockQuote handles the speed-critical RFP-to-quote workflow." data-id="element-3283" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3284">
            <motion.div variants={fadeUp} custom={0} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} className="border border-stone-800 bg-stone-900/40 p-8" data-id="element-3285">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-stone-800" data-id="element-3286">
                <Cloud className="w-5 h-5 text-stone-300" data-id="element-3287" />
                <p className="font-mono text-xs uppercase tracking-widest text-stone-400" data-id="element-3288">
                  Stays in Salesforce
                </p>
              </div>
              <ul className="space-y-3 text-stone-300" data-id="element-3289">
                {['Account & contact records', 'Opportunity pipeline & forecasting', 'Sales team activity & tasks', 'Reporting, dashboards, KPIs', 'Integrations with finance & marketing', 'Long-term relationship history'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-3290">
                    <CheckCircle2 className="w-4 h-4 text-stone-500 mt-1 flex-shrink-0" data-id="element-3291" />
                    <span className="text-sm" data-id="element-3292">{item}</span>
                  </li>)}
              </ul>
            </motion.div>

            <motion.div variants={fadeUp} custom={1} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} className="border border-red-900/40 bg-red-950/10 p-8" data-id="element-3293">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-stone-800" data-id="element-3294">
                <Zap className="w-5 h-5 text-red-500" data-id="element-3295" />
                <p className="font-mono text-xs uppercase tracking-widest text-red-500" data-id="element-3296">
                  Lives in BlockQuote
                </p>
              </div>
              <ul className="space-y-3 text-stone-200" data-id="element-3297">
                {['RFP intake & structured request building', 'Rate logic, package generation, pricing rules', 'Hold lifecycle & expiry management', 'Contract & deposit tracking', 'Quote-level audit trail', 'Agency-facing portal & branded proposals'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-3298">
                    <CheckCircle2 className="w-4 h-4 text-red-500 mt-1 flex-shrink-0" data-id="element-3299" />
                    <span className="text-sm" data-id="element-3300">{item}</span>
                  </li>)}
              </ul>
            </motion.div>
          </div>
        </section>

        {/* SECTION 2 — Data Flow Diagram */}
        <section data-id="element-3301">
          <SectionHeader num="02" eyebrow="Data Flow" title="How information moves between the systems" subtitle="A minimal, reversible, non-disruptive flow. No Salesforce schema changes are required for the pilot." data-id="element-3302" />

          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="border border-stone-800 bg-stone-900/30 p-10 md:p-14" data-id="element-3303">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto_1fr] items-center gap-6 md:gap-4" data-id="element-3304">
              {/* Box 1 */}
              <div className="border border-stone-700 bg-stone-950 p-6 text-center" data-id="element-3305">
                <div className="w-12 h-12 mx-auto mb-4 border border-stone-700 flex items-center justify-center" data-id="element-3306">
                  <Users className="w-5 h-5 text-stone-300" data-id="element-3307" />
                </div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-stone-500 mb-2" data-id="element-3308">
                  Source
                </p>
                <p className="font-serif text-lg text-stone-50 mb-1" data-id="element-3309">
                  Agency / Planner / Direct Guest
                </p>
                <p className="text-xs text-stone-400" data-id="element-3310">
                  Sends RFP via email, form, or WhatsApp
                </p>
              </div>

              {/* Arrow 1 */}
              <div className="hidden md:flex flex-col items-center" data-id="element-3311">
                <ArrowRight className="w-6 h-6 text-red-600" data-id="element-3312" />
                <p className="font-mono text-[10px] uppercase tracking-widest text-stone-500 mt-2" data-id="element-3313">
                  Intake
                </p>
              </div>
              <div className="md:hidden flex items-center justify-center text-red-600" data-id="element-3314">
                <ArrowRight className="w-5 h-5 rotate-90" data-id="element-3315" />
              </div>

              {/* Box 2 — BlockQuote (highlighted) */}
              <div className="border-2 border-red-600 bg-red-950/20 p-6 text-center relative" data-id="element-3316">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-red-600 px-3 py-0.5 font-mono text-[10px] uppercase tracking-widest text-white" data-id="element-3317">
                  Quoting Rail
                </div>
                <div className="w-12 h-12 mx-auto mb-4 bg-red-600 flex items-center justify-center" data-id="element-3318">
                  <Zap className="w-5 h-5 text-white" data-id="element-3319" />
                </div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-red-400 mb-2" data-id="element-3320">
                  Workflow Layer
                </p>
                <p className="font-serif text-lg text-stone-50 mb-1" data-id="element-3321">
                  BlockQuote
                </p>
                <p className="text-xs text-stone-300" data-id="element-3322">
                  Structures RFP, generates quotes, manages holds & contracts
                </p>
              </div>

              {/* Arrow 2 */}
              <div className="hidden md:flex flex-col items-center" data-id="element-3323">
                <ArrowRight className="w-6 h-6 text-red-600" data-id="element-3324" />
                <p className="font-mono text-[10px] uppercase tracking-widest text-stone-500 mt-2" data-id="element-3325">
                  Sync
                </p>
              </div>
              <div className="md:hidden flex items-center justify-center text-red-600" data-id="element-3326">
                <ArrowRight className="w-5 h-5 rotate-90" data-id="element-3327" />
              </div>

              {/* Box 3 */}
              <div className="border border-stone-700 bg-stone-950 p-6 text-center" data-id="element-3328">
                <div className="w-12 h-12 mx-auto mb-4 border border-stone-700 flex items-center justify-center" data-id="element-3329">
                  <Cloud className="w-5 h-5 text-stone-300" data-id="element-3330" />
                </div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-stone-500 mb-2" data-id="element-3331">
                  System of Record
                </p>
                <p className="font-serif text-lg text-stone-50 mb-1" data-id="element-3332">
                  Salesforce
                </p>
                <p className="text-xs text-stone-400" data-id="element-3333">
                  Receives Deal ID, status, timestamps, link
                </p>
              </div>
            </div>

            <p className="text-center text-sm text-stone-500 mt-10 italic max-w-2xl mx-auto" data-id="element-3334">
              Minimal, reversible, non-disruptive. The pilot can be turned off
              at any time without affecting your Salesforce data model.
            </p>
          </motion.div>
        </section>

        {/* SECTION 3 — What we write */}
        <section id="what-we-sync" data-id="element-3335">
          <SectionHeader num="03" eyebrow="Write Path" title="What we write to Salesforce" subtitle="A small, well-defined set of fields. Everything is namespaced and tagged so it can be removed cleanly." data-id="element-3336" />

          <div className="border border-stone-800 overflow-hidden" data-id="element-3337">
            <div className="grid grid-cols-12 bg-stone-900 border-b border-stone-800" data-id="element-3338">
              <div className="col-span-4 p-4 font-mono text-xs uppercase tracking-widest text-stone-400" data-id="element-3339">
                Field
              </div>
              <div className="col-span-3 p-4 font-mono text-xs uppercase tracking-widest text-stone-400" data-id="element-3340">
                Type
              </div>
              <div className="col-span-5 p-4 font-mono text-xs uppercase tracking-widest text-stone-400" data-id="element-3341">
                Description
              </div>
            </div>

            {[{
            field: 'bq_deal_id',
            type: 'String',
            desc: 'BlockQuote deal identifier (unique, immutable)'
          }, {
            field: 'bq_property',
            type: 'String',
            desc: 'Resort property name'
          }, {
            field: 'bq_dates',
            type: 'Date range',
            desc: 'Check-in → check-out'
          }, {
            field: 'bq_rooms_total',
            type: 'Integer',
            desc: 'Total room count (room mix optional)'
          }, {
            field: 'bq_status',
            type: 'Enum',
            desc: 'Submitted · Quoted · Hold Requested · Hold Approved · Contracted · Closed Won · Lost'
          }, {
            field: 'bq_submitted_at',
            type: 'Timestamp',
            desc: 'RFP received'
          }, {
            field: 'bq_first_quote_at',
            type: 'Timestamp',
            desc: 'First quote sent'
          }, {
            field: 'bq_hold_requested_at',
            type: 'Timestamp',
            desc: 'Hold requested by agency'
          }, {
            field: 'bq_hold_approved_at',
            type: 'Timestamp',
            desc: 'Hold confirmed by resort'
          }, {
            field: 'bq_deal_url',
            type: 'URL',
            desc: 'Deep link back to BlockQuote deal'
          }].map((row, i) => <div key={i} className={`grid grid-cols-12 ${i % 2 === 0 ? 'bg-stone-900/20' : 'bg-stone-950'} border-b border-stone-900 last:border-b-0`} data-id="element-3342">
                <div className="col-span-4 p-4 font-mono text-sm text-red-400" data-id="element-3343">
                  {row.field}
                </div>
                <div className="col-span-3 p-4 font-mono text-xs text-stone-400 uppercase tracking-widest flex items-center" data-id="element-3344">
                  {row.type}
                </div>
                <div className="col-span-5 p-4 text-sm text-stone-300" data-id="element-3345">
                  {row.desc}
                </div>
              </div>)}
          </div>
        </section>

        {/* SECTION 4 — What we read */}
        <section data-id="element-3346">
          <SectionHeader num="04" eyebrow="Read Path" title="What we read from Salesforce (minimal)" subtitle="Read access is intentionally narrow. The fewer fields we touch, the easier the pilot is to approve." data-id="element-3347" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-3348">
            {[{
            icon: Users,
            title: 'Contact identity',
            desc: 'Name and email of the requesting contact (so quotes route to the right person).'
          }, {
            icon: Database,
            title: 'Account / agency identifier',
            desc: 'Optional. Links the BlockQuote agency profile to the Salesforce account.'
          }, {
            icon: Layers,
            title: 'Segment tag',
            desc: 'Optional. Lets us route by business segment (wedding, corporate, incentive).'
          }].map((item, i) => <motion.div key={i} variants={fadeUp} custom={i} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} className="border border-stone-800 bg-stone-900/30 p-6" data-id="element-3349">
                <item.icon className="w-5 h-5 text-red-500 mb-4" data-id="element-3350" />
                <h3 className="font-serif text-lg text-stone-50 mb-2" data-id="element-3351">
                  {item.title}
                </h3>
                <p className="text-sm text-stone-400 leading-relaxed" data-id="element-3352">
                  {item.desc}
                </p>
              </motion.div>)}
          </div>
        </section>

        {/* SECTION 5 — Three integration modes */}
        <section data-id="element-3353">
          <SectionHeader num="05" eyebrow="Integration Modes" title="Three ways to connect — pick your pace" subtitle="Start light, expand only if and when you want to. The pilot recommendation is Mode A." data-id="element-3354" />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-3355">
            {/* A */}
            <motion.div variants={fadeUp} custom={0} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} className="border-2 border-red-600 bg-red-950/10 p-7 flex flex-col relative" data-id="element-3356">
              <div className="absolute -top-3 left-7 bg-red-600 px-3 py-0.5 font-mono text-[10px] uppercase tracking-widest text-white" data-id="element-3357">
                Recommended for pilot
              </div>
              <Link2 className="w-6 h-6 text-red-500 mb-5" data-id="element-3358" />
              <p className="font-mono text-xs uppercase tracking-widest text-red-400 mb-2" data-id="element-3359">
                Mode A
              </p>
              <h3 className="font-serif text-2xl text-stone-50 mb-3" data-id="element-3360">
                Link + Status Sync
              </h3>
              <p className="text-sm text-stone-300 mb-5 leading-relaxed" data-id="element-3361">
                Fastest path. Write Deal ID, status, timestamps, and a deep link
                back to BlockQuote as fields on the existing Opportunity.
              </p>
              <ul className="space-y-2 text-sm text-stone-300 mb-6" data-id="element-3362">
                <li className="flex items-center gap-2" data-id="element-3363">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" data-id="element-3364" />
                  No new Salesforce objects
                </li>
                <li className="flex items-center gap-2" data-id="element-3365">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" data-id="element-3366" />
                  Stand up in days, not weeks
                </li>
                <li className="flex items-center gap-2" data-id="element-3367">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" data-id="element-3368" />
                  Fully reversible
                </li>
              </ul>
              <div className="mt-auto pt-5 border-t border-red-900/40" data-id="element-3369">
                <p className="font-mono text-[10px] uppercase tracking-widest text-stone-500" data-id="element-3370">
                  Setup time
                </p>
                <p className="font-mono text-stone-50 text-sm mt-1" data-id="element-3371">
                  ~3–5 days
                </p>
              </div>
            </motion.div>

            {/* B */}
            <motion.div variants={fadeUp} custom={1} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} className="border border-stone-800 bg-stone-900/30 p-7 flex flex-col" data-id="element-3372">
              <Layers className="w-6 h-6 text-stone-300 mb-5" data-id="element-3373" />
              <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-2" data-id="element-3374">
                Mode B
              </p>
              <h3 className="font-serif text-2xl text-stone-50 mb-3" data-id="element-3375">
                Custom Object
              </h3>
              <p className="text-sm text-stone-400 mb-5 leading-relaxed" data-id="element-3376">
                A dedicated{' '}
                <span className="font-mono text-stone-300" data-id="element-3377">BQ_Deal__c</span>{' '}
                object that mirrors quote-level detail with full history and
                relationships to existing Accounts.
              </p>
              <ul className="space-y-2 text-sm text-stone-400 mb-6" data-id="element-3378">
                <li className="flex items-center gap-2" data-id="element-3379">
                  <CheckCircle2 className="w-3.5 h-3.5 text-stone-500" data-id="element-3380" />
                  Best for reporting & dashboards
                </li>
                <li className="flex items-center gap-2" data-id="element-3381">
                  <CheckCircle2 className="w-3.5 h-3.5 text-stone-500" data-id="element-3382" />
                  Tracks all quote versions
                </li>
                <li className="flex items-center gap-2" data-id="element-3383">
                  <CheckCircle2 className="w-3.5 h-3.5 text-stone-500" data-id="element-3384" />
                  Native rollups & formulas
                </li>
              </ul>
              <div className="mt-auto pt-5 border-t border-stone-800" data-id="element-3385">
                <p className="font-mono text-[10px] uppercase tracking-widest text-stone-500" data-id="element-3386">
                  Setup time
                </p>
                <p className="font-mono text-stone-50 text-sm mt-1" data-id="element-3387">
                  ~2–3 weeks
                </p>
              </div>
            </motion.div>

            {/* C */}
            <motion.div variants={fadeUp} custom={2} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} className="border border-stone-800 bg-stone-900/30 p-7 flex flex-col" data-id="element-3388">
              <Cable className="w-6 h-6 text-stone-300 mb-5" data-id="element-3389" />
              <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-2" data-id="element-3390">
                Mode C
              </p>
              <h3 className="font-serif text-2xl text-stone-50 mb-3" data-id="element-3391">
                Middleware
              </h3>
              <p className="text-sm text-stone-400 mb-5 leading-relaxed" data-id="element-3392">
                Enterprise path via MuleSoft, Workato, or Boomi — fits inside
                your existing integration governance and security review.
              </p>
              <ul className="space-y-2 text-sm text-stone-400 mb-6" data-id="element-3393">
                <li className="flex items-center gap-2" data-id="element-3394">
                  <CheckCircle2 className="w-3.5 h-3.5 text-stone-500" data-id="element-3395" />
                  Centralized integration ops
                </li>
                <li className="flex items-center gap-2" data-id="element-3396">
                  <CheckCircle2 className="w-3.5 h-3.5 text-stone-500" data-id="element-3397" />
                  Custom routing & transforms
                </li>
                <li className="flex items-center gap-2" data-id="element-3398">
                  <CheckCircle2 className="w-3.5 h-3.5 text-stone-500" data-id="element-3399" />
                  Enterprise SLAs
                </li>
              </ul>
              <div className="mt-auto pt-5 border-t border-stone-800" data-id="element-3400">
                <p className="font-mono text-[10px] uppercase tracking-widest text-stone-500" data-id="element-3401">
                  Setup time
                </p>
                <p className="font-mono text-stone-50 text-sm mt-1" data-id="element-3402">
                  4–8 weeks
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* SECTION 6 — Security */}
        <section data-id="element-3403">
          <SectionHeader num="06" eyebrow="Security & Governance" title="Built for enterprise review" subtitle="Every design choice optimizes for a fast, clean security review." data-id="element-3404" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3405">
            {[{
            icon: Lock,
            title: 'Tenant isolation',
            desc: 'Each resort is a fully isolated tenant. No cross-tenant data access at any layer.'
          }, {
            icon: Users,
            title: 'Role-based access',
            desc: 'Granular roles: Resort Admin, Resort Staff, Agency Admin, Agency Agent, Super Admin.'
          }, {
            icon: FileCheck2,
            title: 'Audit logs',
            desc: 'Every quote, status change, and sync event is logged with user, timestamp, and diff.'
          }, {
            icon: Shield,
            title: 'AI guardrails',
            desc: 'AI assists with intake and follow-up. Pricing is deterministic — rules decide, never AI.'
          }, {
            icon: KeyRound,
            title: 'OAuth + scoped permissions',
            desc: 'Connect via OAuth 2.0 with the smallest possible Salesforce permission set.'
          }, {
            icon: Database,
            title: 'Data residency',
            desc: 'Hosted in your region. No data leaves the agreed boundary without explicit approval.'
          }].map((item, i) => <motion.div key={i} variants={fadeUp} custom={i} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} className="border border-stone-800 bg-stone-900/30 p-6 flex items-start gap-4" data-id="element-3406">
                <item.icon className="w-5 h-5 text-red-500 mt-1 flex-shrink-0" data-id="element-3407" />
                <div data-id="element-3408">
                  <h3 className="font-medium text-stone-50 mb-1.5" data-id="element-3409">
                    {item.title}
                  </h3>
                  <p className="text-sm text-stone-400 leading-relaxed" data-id="element-3410">
                    {item.desc}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </section>

        {/* SECTION 7 — What we DON'T do */}
        <section data-id="element-3411">
          <SectionHeader num="07" eyebrow="Pilot Scope" title="What we don't do in the pilot" subtitle="Just as important as what we do. These boundaries reduce risk and shorten approval." data-id="element-3412" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3413">
            {[{
            title: 'Not replacing Salesforce',
            desc: 'Salesforce stays the system of record. We never compete for that role.'
          }, {
            title: 'Not building pricing logic in Salesforce',
            desc: 'Rate logic lives in BlockQuote. Salesforce never has to know how a quote was priced.'
          }, {
            title: 'Not requiring deep PMS integration day 1',
            desc: 'PMS sync is a Phase 2 conversation. The pilot runs on rate-sheet inputs.'
          }, {
            title: 'No sensitive guest PII ingestion',
            desc: 'We only ingest the minimum data needed to quote — no passports, no payment data.'
          }].map((item, i) => <motion.div key={i} variants={fadeUp} custom={i} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} className="border border-stone-800 bg-stone-900/30 p-6 flex items-start gap-4" data-id="element-3414">
                <XCircle className="w-5 h-5 text-stone-500 mt-1 flex-shrink-0" data-id="element-3415" />
                <div data-id="element-3416">
                  <h3 className="font-medium text-stone-50 mb-1.5" data-id="element-3417">
                    {item.title}
                  </h3>
                  <p className="text-sm text-stone-400 leading-relaxed" data-id="element-3418">
                    {item.desc}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </section>

        {/* SECTION 8 — Pilot deliverables */}
        <section data-id="element-3419">
          <SectionHeader num="08" eyebrow="Pilot Deliverables" title="What you walk away with" subtitle="A working system, clean integration points, and the data to make a scale decision." data-id="element-3420" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-3421">
            {[{
            icon: Workflow,
            title: 'Standalone portal + workflow',
            desc: 'Branded resort & agency views, live for the pilot segment.'
          }, {
            icon: Cable,
            title: 'Salesforce sync fields',
            desc: 'Deal ID, status, timestamps, and link written back automatically.'
          }, {
            icon: Database,
            title: 'KPI dashboard',
            desc: 'Time-to-quote, hold rate, contract rate, fully booked revenue.'
          }, {
            icon: Calendar,
            title: 'Weekly cadence',
            desc: 'Live working sessions to tune the workflow against real deals.'
          }].map((item, i) => <motion.div key={i} variants={fadeUp} custom={i} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} className="border border-stone-800 bg-stone-900/30 p-6" data-id="element-3422">
                <item.icon className="w-5 h-5 text-red-500 mb-4" data-id="element-3423" />
                <h3 className="font-medium text-stone-50 mb-2" data-id="element-3424">{item.title}</h3>
                <p className="text-sm text-stone-400 leading-relaxed" data-id="element-3425">
                  {item.desc}
                </p>
              </motion.div>)}
          </div>
        </section>
      </div>

      {/* CTA STRIP */}
      <section className="border-t border-stone-900 bg-gradient-to-b from-stone-950 to-red-950/20" data-id="element-3426">
        <div className="max-w-6xl mx-auto px-6 py-24" data-id="element-3427">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="text-center" data-id="element-3428">
            <p className="font-mono text-xs uppercase tracking-widest text-red-500 mb-6" data-id="element-3429">
              Next Step
            </p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-stone-50 leading-tight mb-6 max-w-3xl mx-auto" data-id="element-3430">
              Hit 4-hour quoting without disrupting Salesforce.
            </h2>
            <p className="text-lg text-stone-400 max-w-2xl mx-auto" data-id="element-3431">
              The fastest pilots start with a 30-minute working session to map
              the Salesforce sync fields against your existing Opportunity
              schema.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-stone-900 py-10" data-id="element-3432">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-3433">
          <p className="font-mono text-xs text-stone-600" data-id="element-3434">
            © 2026 BlockQuote. Salesforce-safe by design.
          </p>
          <Link to="/" className="font-mono text-xs uppercase tracking-widest text-stone-500 hover:text-stone-300 transition-colors flex items-center gap-2" data-id="element-3435">
            <ArrowLeft className="w-3.5 h-3.5" data-id="element-3436" />
            Back to site
          </Link>
        </div>
      </footer>
    </div>;
}